import { useGesture, UserHandlers } from "@use-gesture/react";
import { useCallback, useMemo } from "react";

import { useDashboardContext } from "../../../contexts/dashboard-context";
import { usePreviewContext } from "../../../contexts/preview-context";
import { useBottomMenu } from "../../../hooks/useBottomMenu";
import { useDefinition } from "../../../hooks/useDefinition";
import { useGoogleAnalytics } from "../../../hooks/useGoogleAnalytics";
import { useIsPreviewMode } from "../../../hooks/useIsPreviewMode";
import { useIsTextEditMode } from "../../../hooks/useIsTextEditMode";
import { useIsTransformMode } from "../../../hooks/useIsTransformMode";
import { useSelectedSpread, useSelectedZone } from "../../../hooks/useSelection";
import { IDragImageRef } from "../../drag-image";
import { REMEMBER_NEXT_SPREADS_TOOLTIP } from "../../navigation-bar/constants";
import { usePhotoPanning } from "../hooks/use-photo-panning";
import { usePhotoPinching } from "../hooks/use-photo-pinching";
import { usePhotoSwapping } from "../hooks/use-photo-swapping";
import { usePreviewPanning, usePreviewPinching } from "../hooks/use-preview-pan-pinch";
import { useZoneMove } from "../hooks/use-zone-move";
import { useZoneResize } from "../hooks/use-zone-resize";

function isInPhotoSelectionPanel(el: Element | null | undefined) {
	while (el) {
		if (el.classList.contains("photo-selection-panel")) return true;
		el = el.parentElement;
	}
	return false;
}

export function useBindSlideshowGestures(dragImageRef: React.RefObject<IDragImageRef>) {
	// Return to caller and trigger a rerender
	const googleAnalytics = useGoogleAnalytics();
	const { slideShowPreviewRef, getZonesAtPoint, isZoomed, goToNext, goToPrev } =
		usePreviewContext();
	const selectedZone = useSelectedZone();

	const dashboard = useDashboardContext();
	const { stack } = useBottomMenu();

	const selectedSpread = useSelectedSpread();
	const [isPreviewMode] = useIsPreviewMode();
	const definition = useDefinition();
	const isPreviewMandatory = definition?.product.isPreviewRequiredMC;
	const hideNextSpreadsTooltip = sessionStorage.getItem(REMEMBER_NEXT_SPREADS_TOOLTIP);

	const [isTransformMode] = useIsTransformMode();

	// Support Drag / Swipe
	// ---------------------
	const [isPhotoPanning, photoPanning] = usePhotoPanning({
		maxReplaceDelay: Infinity,
	});
	const [isPhotoSwapping, photoSwapping] = usePhotoSwapping(dragImageRef);
	const [isPhotoPinching, photoPinching] = usePhotoPinching({
		maxReplaceDelay: Infinity,
	});
	const [isPreviewPinching, previewPinching] = usePreviewPinching();
	const [isPreviewPanning, previewPanning] = usePreviewPanning();
	const [isZoneMoving, zoneMoving] = useZoneMove();
	const [isZoneResizing] = useZoneResize(); // Zone resize is triggered from the resize handlers UI
	const isTextEditMode = useIsTextEditMode();

	// This won't work: const slideShowPreviewElRef = useRef(slideShowPreviewRef.current?.previewEl);
	// because this uses the previewEl at the start = null
	/** Create a ref object that returns the current slideshow element */
	const slideShowPreviewElRef: React.RefObject<HTMLDivElement> = useMemo(
		() => ({
			get current() {
				return slideShowPreviewRef.current?.previewEl ?? null;
			},
		}),
		[slideShowPreviewRef],
	);

	function isMultiTouch(event: PointerEvent | MouseEvent | TouchEvent | KeyboardEvent) {
		return (event as TouchEvent).touches?.length > 1;
	}

	// LONG PRESS EVENTS (Swap)
	// Do before the other useGesture!
	// ------------------
	const longPressDragStart = useCallback<UserHandlers["onDragStart"]>(
		function longPressDragStart(arg) {
			if (isTransformMode) return;
			const {
				xy: [x, y],
				elapsedTime,
			} = arg;

			// To only detect long press and not drag 'moving' or 'pinching' time
			if (elapsedTime !== 0 || isMultiTouch(arg.event) || isPreviewMode) return;

			const startZone = getZonesAtPoint(
				{ x, y },
				(z) =>
					eXpress.core.isPhotoZone(z) && !z.isEmpty && dashboard.canApplyPhotoToZone(z),
			).last();

			if (startZone && !isTextEditMode) photoSwapping.start(startZone, x, y);
		},
		[isPreviewMode, getZonesAtPoint, photoSwapping, dashboard, isTransformMode, isTextEditMode],
	);

	// After long Press
	useGesture(
		{
			onDragStart: longPressDragStart,
		},
		{
			target: slideShowPreviewElRef,
			drag: {
				pointer: {
					touch: true,
				},
				// adapt this value to change the longpress delay
				delay: 400,
			},
		},
	);

	const onDragStart = useCallback<UserHandlers["onDragStart"]>(
		function onDragStart(arg) {
			// On DragStart, cancel any leftovers
			photoPanning.cancel();
			zoneMoving.cancel();
			previewPanning.cancel();
			previewPinching.cancel();

			// If already pinching, don't start drag related things
			if (isPreviewPinching) return;
			if (isPhotoPinching) return;

			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const {
				xy: [x, y],
			} = arg;
			const pointOnScreen = eXpress.math.point(x, y);

			// Get Slideshow item clicked (contains the spread)
			const selectedInfo = slideShow.getSlideShowItemAtPoint(pointOnScreen);

			// Only use the selected zone for panning when drag starts on this zone.
			const clickedZones = selectedInfo
				? selectedInfo.item.getZonesAtPoint(selectedInfo.point)
				: undefined;
			const isSelectedZoneTouched =
				clickedZones && selectedZone ? clickedZones.includes(selectedZone) : false;

			// Move Zone?
			const canMoveZone =
				!isPreviewMode && // Don't allow in Preview Mode
				!isTransformMode && // In Transform mode, we will allow panning
				!isPhotoSwapping && // Already swapping?
				!isZoneResizing &&
				isSelectedZoneTouched && // Only if starting a drag on the selected photo zone
				selectedInfo &&
				selectedZone?.canChangeTransform() &&
				!isTextEditMode;

			if (canMoveZone) {
				zoneMoving.start({
					selectedZone: selectedZone as eXpress.core.ITransformableZone, // Type checked in canMoveZone
					pointOnScreen,
				});
				return;
			}

			// Pan Photo in Zone?
			const canPanPhoto =
				!isPreviewMode && // Don't allow in Preview Mode
				isTransformMode &&
				selectedInfo &&
				dashboard.canPanPhoto(selectedZone) &&
				selectedZone.getPhotoLook()?.photo.state !== eXpress.core.PhotoState.Rejected; // Prevent panning for rejected photo's

			if (canPanPhoto) {
				photoPanning.start({
					selectedZone,
					pointOnScreen,
				});
				return;
			}

			// Pan Preview?
			const canPanPreview =
				selectedSpread &&
				!isTransformMode &&
				!isZoneResizing &&
				!isPhotoSwapping &&
				isZoomed(selectedSpread) && // Only allow panning when zoomed
				!stack.some((item) => item.id === "text-value"); // don't allow panning when editing text

			if (canPanPreview) {
				previewPanning.start(selectedSpread);
			}
		},
		[
			photoPanning,
			previewPanning,
			zoneMoving,
			previewPinching,
			dashboard,
			slideShowPreviewRef,
			selectedSpread,
			selectedZone,
			stack,
			isPreviewMode,
			isZoomed,
			isTransformMode,
			isZoneResizing,
			isPreviewPinching,
			isPhotoSwapping,
			isPhotoPinching,
			isTextEditMode,
		],
	);

	const onDrag = useCallback<UserHandlers["onDrag"]>(
		function onDrag(arg) {
			const {
				xy: [x, y],
			} = arg;

			// Photo swapping, check as first!
			if (isPhotoSwapping) {
				const aboveZone = getZonesAtPoint({ x, y }, dashboard.canApplyPhotoToZone).last();
				photoSwapping.update(aboveZone, x, y);
			} else if (isPhotoPanning) {
				// Photo panning
				photoPanning.update(eXpress.math.point(x, y));
			} else if (isZoneMoving) {
				// Zone moving
				zoneMoving.update(eXpress.math.point(x, y));
			} else {
				// Preview panning
				const {
					movement: [dx, dy],
				} = arg;
				if (!selectedSpread) return;
				/* Still needed?
			// Only start a new preview panning when nothing is busy
			if (
				!previewPinching.isPinching()
				&& !previewPanning.isPanning()
				&& !photoPanning.isPhotoPanning()
				&& !photoSwapping.isPhotoSwapping()
			) {
				const minDragDistance = 3;
				if (Math.abs(dx) > minDragDistance || Math.abs(dy) > minDragDistance) {
					previewPanning.start(selectedSpread);
				}
			}
			*/

				// Panning preview
				if (isPreviewPanning) {
					previewPanning.update(dx, dy);
				}
			}
		},
		[
			photoPanning,
			previewPanning,
			photoSwapping,
			zoneMoving,
			selectedSpread,
			dashboard,
			getZonesAtPoint,
			isZoneMoving,
			isPhotoPanning,
			isPreviewPanning,
			isPhotoSwapping,
		],
	);

	const onDragEnd = useCallback<UserHandlers["onDragEnd"]>(
		function onDragEnd(arg) {
			let canTrySwipe = true;

			// Swap Photo
			if (isPhotoSwapping) {
				const {
					xy: [x, y],
				} = arg;
				const endZone = getZonesAtPoint({ x, y }, dashboard.canApplyPhotoToZone).last();

				// Dropped on a Zone?
				if (endZone) {
					photoSwapping.end(endZone);
				} else {
					// Dropped on the photo-selection?
					const aboveEl = document.elementFromPoint(x, y);
					const isDroppedOnPhotoSelection = isInPhotoSelectionPanel(aboveEl);

					if (isDroppedOnPhotoSelection) {
						// TODO: fly to photo in photo-selection?
						photoSwapping.end(null); // empty photozone
					} else {
						photoSwapping.end(undefined); // Animate back to startZone
					}
				}

				canTrySwipe = false;
			}

			// Is panning photo in zone
			if (isPhotoPanning) {
				photoPanning.end();
				canTrySwipe = false;
			}

			// Zone moving
			if (isZoneMoving) {
				zoneMoving.end();
				canTrySwipe = false;
			}

			// Is panning preview?
			if (isPreviewPanning) {
				const {
					movement: [dx, dy],
				} = arg;
				previewPanning.end(dx, dy);
				canTrySwipe = false;
			}

			const canSwipe =
				canTrySwipe &&
				!isTransformMode &&
				!isZoomed(selectedSpread) && // Not zoomed
				!stack.some((item) => item.id === "text-value"); // Not editing text

			// Don't use swipe gesture if zoomed, we will use preview panning
			if (canSwipe) {
				const {
					movement: [offsetX, offsetY],
					elapsedTime,
				} = arg;
				const minDistance = 80;
				if (Math.abs(offsetY) > minDistance) return; // Horizontal scroll (max 45°)
				if (elapsedTime > 500) return; // Add a time limit
				if (offsetX < -minDistance) {
					if (goToNext()) {
						if (isPreviewMandatory && !hideNextSpreadsTooltip && isPreviewMode) {
							sessionStorage.setItem(REMEMBER_NEXT_SPREADS_TOOLTIP, "true");
						}
						googleAnalytics.trackEventToGa({
							trackingAction: "LeftSwipe",
						});
					}
				} else if (offsetX > minDistance) {
					if (goToPrev()) {
						if (isPreviewMandatory && !hideNextSpreadsTooltip && isPreviewMode) {
							sessionStorage.setItem(REMEMBER_NEXT_SPREADS_TOOLTIP, "true");
						}
						googleAnalytics.trackEventToGa({
							trackingAction: "RightSwipe",
						});
					}
				}
			}
		},
		[
			photoSwapping,
			photoPanning,
			previewPanning,
			zoneMoving,
			goToNext,
			goToPrev,
			getZonesAtPoint,
			dashboard.canApplyPhotoToZone,
			selectedSpread,
			stack,
			hideNextSpreadsTooltip,
			googleAnalytics,
			isZoomed,
			isPreviewMandatory,
			isPreviewMode,
			isTransformMode,
			isZoneMoving,
			isPhotoPanning,
			isPreviewPanning,
			isPhotoSwapping,
		],
	);

	const onPinch = useCallback<UserHandlers["onPinch"]>(
		function onPinch(arg) {
			const slideshow = slideShowPreviewRef.current?.slideShow;
			if (!slideshow) return;

			if (!selectedSpread) return;

			// Don't allow pinch when already panning something
			if (isPhotoPanning) return;
			if (isPhotoSwapping) return;
			if (isPreviewPanning) return;
			if (isZoneMoving) return;
			if (isZoneResizing) return;

			if (isTransformMode) {
				const canZoomPhoto =
					!isPreviewMode && // Don't allow in Preview Mode
					!isPhotoSwapping &&
					!isPhotoPanning &&
					dashboard.canZoomPhoto(selectedZone) &&
					selectedZone.getPhotoLook()?.photo.state !== eXpress.core.PhotoState.Rejected; // Prevent panning for rejected photo's

				if (canZoomPhoto) {
					// https://github.com/pmndrs/use-gesture/issues/260
					// Pinching works differently on Safari w/touchpad vs Chrome&Firefox w/touchpad.
					// Chrome and Firefox translate pinching gesture to a CTRL + Scrollwheel zoom.
					// Using the scale from scroll offset we can pinch on those browsers too because there da[0] is always 0.
					const [scale] = arg.offset;
					// If the first event is a pinch, goto pinch mode
					if (!isPhotoPinching) {
						photoPinching.start(selectedZone, arg.da[0] || scale, arg.origin);
					}

					photoPinching.update(arg.da[0] || scale, arg.origin);
				}
			} else {
				// If the first event is a pinch, goto pinch mode
				if (!isPreviewPinching) {
					previewPinching.start(selectedSpread, arg.da[0], arg.origin);
				}
				previewPinching?.update(arg.da[0], arg.origin);
			}
		},
		[
			dashboard,
			isPhotoPanning,
			isPhotoPinching,
			isPhotoSwapping,
			isPreviewMode,
			isPreviewPanning,
			isPreviewPinching,
			isTransformMode,
			isZoneMoving,
			isZoneResizing,
			photoPinching,
			previewPinching,
			selectedSpread,
			selectedZone,
			slideShowPreviewRef,
		],
	);

	const onPinchEnd = useCallback<UserHandlers["onPinchEnd"]>(() => {
		if (isPreviewPinching) previewPinching.end();
		if (isPhotoPinching) photoPinching.end();
	}, [isPhotoPinching, isPreviewPinching, photoPinching, previewPinching]);

	// Add handlers
	useGesture(
		{
			onDragStart,
			onDrag,
			onDragEnd,

			onPinch,
			onPinchEnd,
		},
		{
			target: slideShowPreviewElRef,
			drag: {
				pointer: {
					touch: true,
				},
				// prevent immediately entering drag behavior when releasing 1 of 2 pinching fingers
				delay: 300,
				// seems to prevent onClick from firing when pinching started (only documented for drag, but seems to work for pinching too)
				filterTaps: true,
			},
		},
	);
}
